/**
 * WeeklyDeliverableEditor.jsx - Editor de Entregables Semanales Colaborativo
 * 
 * Permite a grupos de estudiantes trabajar colaborativamente en entregables semanales:
 * - B1: Visualización de recursos de la semana (videos, lecturas, templates)
 * - B2: Editor colaborativo con sistema de locks para evitar conflictos
 * - B2: Comentarios individuales por miembro del grupo  
 * - B4: Integración Coach IA para feedback y guía (placeholder)
 * - B5: Declaración final grupal con confirmaciones de todos los miembros
 * 
 * Flujo: Recursos → Editor Colaborativo → Comentarios → Coach IA → Declaración Final
 * Solo accesible para miembros del grupo asignado a la sección
 * 
 * @component
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BookOpen,
  Users, 
  Edit3,
  MessageSquare,
  Brain,
  CheckCircle,
  Lock,
  Unlock,
  Save,
  Clock,
  AlertCircle,
  FileText,
  Video,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  User,
  Calendar,
  Target,
  Lightbulb,
  Send,
  Eye,
  ArrowLeft,
  RefreshCw,
  Download,
  Loader2,
  Star,
  ClipboardList,
  Award,
  Zap
} from 'lucide-react';
import apiClient from '../services/apiClient';

const WeeklyDeliverableEditor = () => {
  const { user } = useAuth();
  const { sectionId, groupId, week } = useParams();
  const navigate = useNavigate();
  
  // Estados principales del componente
  const [section, setSection] = useState(null);
  const [group, setGroup] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados del editor colaborativo (B2)
  const [editorContent, setEditorContent] = useState('');
  const [isEditorLocked, setIsEditorLocked] = useState(false);
  const [lockOwner, setLockOwner] = useState(null);
  const [lockOwnerName, setLockOwnerName] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Estados de comentarios (B2)
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(true);

  // Estados del Coach IA (B4)
  const [coachFeedback, setCoachFeedback] = useState(null);
  const [requestingCoachHelp, setRequestingCoachHelp] = useState(false);
  const [showCoachPanel, setShowCoachPanel] = useState(false);

  // Estados de declaración final (B5)
  const [finalDeclaration, setFinalDeclaration] = useState('');
  const [memberApprovals, setMemberApprovals] = useState({});
  const [showFinalDeclaration, setShowFinalDeclaration] = useState(false);
  const [isSubmittingFinal, setIsSubmittingFinal] = useState(false);
  const [b5Template, setB5Template] = useState({ format: 'structured', sections: [] });
  const [b5FormData, setB5FormData] = useState({});
  const [isReopening, setIsReopening] = useState(false);
  const [b5DraftSummary, setB5DraftSummary] = useState('');
  const [b5DraftEditedByName, setB5DraftEditedByName] = useState('');
  const [b5ResetLog, setB5ResetLog] = useState([]);

  // Estados de recursos (B1)
  const [weekResources, setWeekResources] = useState([]);
  const [showResources, setShowResources] = useState(true);
  const [weekTitle, setWeekTitle] = useState('');
  const [learningObjectives, setLearningObjectives] = useState([]);
  const [b2FormFields, setB2FormFields] = useState([]);
  const [b2Instructions, setB2Instructions] = useState('');
  const [b2FormData, setB2FormData] = useState({});

  // Estados del panel revisión docente (B3)
  const [b3Criteria, setB3Criteria] = useState([]);
  const [b3CriteriaScores, setB3CriteriaScores] = useState({});
  const [b3Notes, setB3Notes] = useState('');
  const [b3ReviewedInClass, setB3ReviewedInClass] = useState(false);
  const [isSavingB3, setIsSavingB3] = useState(false);
  const [b3SavedScore, setB3SavedScore] = useState(null);
  const [showB3Panel, setShowB3Panel] = useState(true);

  // Referencias para timeouts y intervalos
  const saveTimeoutRef = useRef(null);
  const lockCheckIntervalRef = useRef(null);

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
    setupLockMonitoring();
    
    return () => {
      // Cleanup: liberar locks y limpiar timers
      if (isEditorLocked && lockOwner === user.id) {
        releaseLock();
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (lockCheckIntervalRef.current) {
        clearInterval(lockCheckIntervalRef.current);
      }
    };
  }, [sectionId, groupId, week]);

  // Auto-guardado cuando hay cambios
  useEffect(() => {
    if (hasUnsavedChanges) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      saveTimeoutRef.current = setTimeout(() => {
        if (!isEditorLocked || lockOwner === user.id) {
          saveContent();
        }
      }, 2000); // Auto-save después de 2 segundos de inactividad
    }
  }, [editorContent, b2FormData, finalDeclaration, b5FormData, hasUnsavedChanges]);

  /**
   * Cargar datos iniciales: sección, grupo, recursos y entregable
   */
  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar información del grupo y sección
      const [sectionResponse, groupResponse, weeklyResponse, resourcesResponse] = await Promise.all([
        apiClient.get(`/sections/${sectionId}`),
        apiClient.get(`/groups/${groupId}`),
        apiClient.get(`/weekly-deliverables/${groupId}/${week}`),
        apiClient.get(`/weekly-deliverables/course-resources/week/${week}`, { params: { groupId } })
      ]);

      setSection(sectionResponse.data);

      // Normalizar members: el endpoint devuelve User poblados { _id, nombre, email }
      // pero el componente espera { userId, userName }
      const rawGroup = groupResponse.data.group;
      const normalizedGroup = rawGroup ? {
        ...rawGroup,
        members: (rawGroup.members || []).map(m => ({
          userId: m._id?.toString() || m.userId || '',
          userName: m.nombre || m.userName || m.name || 'Miembro',
          email: m.email,
          role: m.role
        }))
      } : null;
      setGroup(normalizedGroup);
      setWeeklyData(weeklyResponse.data);
      setWeekResources(resourcesResponse.data.resources || []);
      setWeekTitle(resourcesResponse.data.weekTitle || `Semana ${week}`);
      setLearningObjectives(resourcesResponse.data.learningObjectives || []);
      setB2FormFields(resourcesResponse.data.b2Template?.fields || []);
      setB2Instructions(resourcesResponse.data.b2Template?.instructions || '');
      
      // Cargar template B5 con normalización de secciones
      const b5TemplateData = resourcesResponse.data.b5Template || {};
      setB5Template({
        format: b5TemplateData.format || 'structured',
        markdownContent: b5TemplateData.markdownContent || '',
        jsonSchema: b5TemplateData.jsonSchema || '',
        sections: (b5TemplateData.sections || []).map(section => ({
          ...section,
          fields: section.fields || []
        }))
      });

      // Inicializar criterios B3 y estado previo de revisión docente
      const criteriaFromCourse = resourcesResponse.data.b3Criteria || [];
      setB3Criteria(criteriaFromCourse);
      const existingAudit = weeklyResponse.data.b3_auditChecklist || {};
      if (existingAudit.criteria && existingAudit.criteria.length > 0) {
        const scoresMap = {};
        existingAudit.criteria.forEach(c => { scoresMap[c.name || c.criterionId] = c.score || 0; });
        setB3CriteriaScores(scoresMap);
      }
      setB3Notes(existingAudit.instructorNotes || '');
      setB3ReviewedInClass(existingAudit.reviewedInClass || false);
      setB3SavedScore(existingAudit.overallScore || null);

      // Establecer contenido del editor
      setEditorContent(weeklyResponse.data.content || '');
      
      // Cargar formData si existe (guardado previo)
      if (weeklyResponse.data.b2FormData && typeof weeklyResponse.data.b2FormData === 'object') {
        setB2FormData(weeklyResponse.data.b2FormData);
      }
      
      // Cargar b5FormData si existe
      if (weeklyResponse.data.b5FormData && typeof weeklyResponse.data.b5FormData === 'object') {
        setB5FormData(weeklyResponse.data.b5FormData);
      }
      
      setComments(weeklyResponse.data.comments || []);
      setFinalDeclaration(weeklyResponse.data.finalDeclaration || '');
      setMemberApprovals(weeklyResponse.data.approvals || {});
      setB5DraftSummary(weeklyResponse.data.b5DraftSummary || '');
      setB5DraftEditedByName(weeklyResponse.data.b5DraftEditedByName || '');
      setB5ResetLog(weeklyResponse.data.b5ResetLog || []);
      setLastSaved(weeklyResponse.data.lastModified);

      // Verificar si el usuario está en el grupo
      const isMember = (groupResponse.data.group?.members || []).some(member => member.userId === user.id);
      if (!isMember && user.role !== 'teacher' && user.role !== 'admin') {
        setError('No tienes acceso a este grupo');
        return;
      }

    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Error al cargar los datos del entregable');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Configurar monitoreo de locks para prevenir conflictos
   */
  const setupLockMonitoring = () => {
    lockCheckIntervalRef.current = setInterval(checkLockStatus, 10000); // Verificar cada 10 segundos
  };

  /**
   * Verificar el estado del lock del editor
   */
  const checkLockStatus = async () => {
    try {
      const response = await apiClient.get(`/weekly-deliverables/${groupId}/${week}/lock-status`);
      const { locked, lockOwner: currentLockOwner, lockOwnerName: currentLockOwnerName } = response.data;
      const isTeacherViewing = user?.role === 'teacher' || user?.role === 'admin';

      setIsEditorLocked(locked);
      setLockOwner(currentLockOwner);
      setLockOwnerName(currentLockOwnerName || null);

      // Docentes no necesitan el banner de error — ven el estado en el badge del B2
      if (locked && currentLockOwner !== user.id && !isTeacherViewing) {
        setError(`${currentLockOwnerName || 'Un compañero'} está editando el documento`);
      } else {
        setError(null);
      }
    } catch (error) {
      console.warn('Error checking lock status:', error);
    }
  };

  /**
   * Solicitar lock del editor para edición exclusiva
   */
  const requestLock = async () => {
    try {
      const response = await apiClient.post(`/weekly-deliverables/${groupId}/${week}/lock`);
      if (response.data.success) {
        setIsEditorLocked(true);
        setLockOwner(user.id);
        setError(null);
      }
    } catch (error) {
      setError('No se pudo obtener el permiso de edición');
    }
  };

  /**
   * Liberar lock del editor
   */
  const releaseLock = async () => {
    try {
      await apiClient.delete(`/weekly-deliverables/${groupId}/${week}/lock`);
      setIsEditorLocked(false);
      setLockOwner(null);
    } catch (error) {
      console.warn('Error releasing lock:', error);
    }
  };

  /**
   * Guardar contenido del editor
   */
  const saveContent = async () => {
    try {
      const payload = {
        content: editorContent,
        lastModified: new Date().toISOString()
      };
      
      // Si hay campos B2 dinámicos, guardar formData
      if (b2FormFields.length > 0) {
        payload.b2FormData = b2FormData;
      }
      
      // Guardar declaración final y formData B5
      payload.finalDeclaration = finalDeclaration;
      if (b5Template.sections && b5Template.sections.length > 0) {
        payload.b5FormData = b5FormData;
      }
      
      await apiClient.put(`/weekly-deliverables/${groupId}/${week}`, payload);
      
      setHasUnsavedChanges(false);
      setLastSaved(new Date().toISOString());
      setSuccess('Contenido guardado automáticamente');
      
      // Limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError('Error al guardar el contenido');
    }
  };

  /**
   * Manejar cambios en el editor
   */
  const handleEditorChange = (value) => {
    setEditorContent(value);
    setHasUnsavedChanges(true);
  };

  /**
   * Agregar comentario individual
   */
  const addComment = async () => {
    if (!newComment.trim()) return;

    try {
      const comment = {
        id: Date.now(),
        userId: user.id,
        userName: user.nombre,
        content: newComment,
        timestamp: new Date().toISOString()
      };

      const response = await apiClient.post(`/weekly-deliverables/${groupId}/${week}/comments`, comment);
      
      setComments(prev => [...prev, response.data]);
      setNewComment('');
      setSuccess('Comentario añadido');
    } catch (error) {
      setError('Error al agregar comentario');
    }
  };

  /**
   * Solicitar análisis IA del entregable (B4 - Coach IA Integration)
   */
  const requestCoachHelp = async () => {
    if (!editorContent.trim()) {
      setError('No hay contenido para analizar');
      return;
    }

    setRequestingCoachHelp(true);
    try {
      const response = await apiClient.post('/group-ai-coach/analyze-deliverable', {
        groupId,
        week: parseInt(week),
        content: editorContent
      });

      if (response.data.success) {
        const analysis = response.data.analysis;
        
        // Formatear feedback para mostrar
        const formattedFeedback = `🎯 **PUNTUACIÓN GENERAL: ${analysis.overallScore}/10**

💪 **FORTALEZAS:**
${analysis.strengths.map(s => `• ${s}`).join('\n')}

🔧 **ÁREAS DE MEJORA:**
${analysis.improvementAreas.map(a => `• ${a}`).join('\n')}

📚 **RECOMENDACIONES:**
${analysis.recommendations ? analysis.recommendations.map(r => `• ${r}`).join('\n') : 'Continuar con el buen trabajo'}

💡 **SIGUIENTES PASOS:**
${analysis.nextStepsAction}

---
*Análisis generado por IA • ${new Date().toLocaleString()}*
        `.trim();
        
        setCoachFeedback(formattedFeedback);
        setShowCoachPanel(true);
        setSuccess('Análisis IA completado exitosamente');
      } else {
        // Usar análisis de fallback si está disponible
        if (response.data.fallback) {
          const fallback = response.data.fallback;
          const fallbackFeedback = `⚠️ **ANÁLISIS BÁSICO: ${fallback.overallScore}/10**

${fallback.detailedFeedback}

💡 **SIGUIENTES PASOS:**
${fallback.nextStepsAction}

---
*Análisis automático básico • Recomendamos solicitar feedback más detallado*
          `.trim();
          
          setCoachFeedback(fallbackFeedback);
          setShowCoachPanel(true);
        } else {
          setError('Error en el análisis IA');
        }
      }
    } catch (error) {
      console.error('Error solicitando coach IA:', error);
      setError('Error conectando con el Coach IA. Intenta nuevamente.');
    } finally {
      setRequestingCoachHelp(false);
    }
  };

  /**
   * Aprobar declaración final (B5)
   */
  const approveFinalDeclaration = async () => {
    try {
      const approval = {
        userId: user.id,
        userName: user.nombre,
        timestamp: new Date().toISOString()
      };

      await apiClient.post(`/weekly-deliverables/${groupId}/${week}/approve`, approval);
      
      setMemberApprovals(prev => ({
        ...prev,
        [user.id]: approval
      }));

      setSuccess('Declaración aprobada');
    } catch (error) {
      setError('Error al aprobar la declaración');
    }
  };

  /**
   * Enviar declaración final cuando todos aprueban
   */
  const submitFinalDeclaration = async () => {
    setIsSubmittingFinal(true);
    try {
      await apiClient.post(`/weekly-deliverables/${groupId}/${week}/submit`, {
        finalDeclaration,
        approvals: memberApprovals,
        submittedAt: new Date().toISOString()
      });

      setSuccess('Entregable enviado exitosamente');
      // Redirect a vista de grupo después de envío exitoso
      setTimeout(() => navigate(`/teaching/sections/${sectionId}/groups`), 2000);
    } catch (error) {
      setError('Error al enviar el entregable final');
    } finally {
      setIsSubmittingFinal(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando entregable semanal...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !group) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button 
            onClick={() => navigate(`/teaching/sections/${sectionId}/groups`)}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Volver a grupos
          </button>
        </div>
      </div>
    );
  }

  const allMembersApproved = group?.members?.length > 0 && 
    group.members.every(member => memberApprovals[member.userId]);

  const isTeacher = user?.role === 'teacher' || user?.role === 'admin';

  /**
   * Reabrir semana finalizada — solo docente.
   * Restaura status a InProgress y limpia confirmaciones B5.
   */
  const handleReopenDeliverable = async () => {
    if (!window.confirm('¿Reabrir este entregable? El grupo podrá volver a editar y deberá confirmar de nuevo la declaración B5.')) return;
    try {
      setIsReopening(true);
      await apiClient.post(`/weekly-deliverables/${groupId}/${week}/reopen`);
      setSuccess('Entregable reabierto. El grupo puede volver a editar.');
      // Recargar datos para reflejar el nuevo estado
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al reabrir el entregable');
    } finally {
      setIsReopening(false);
    }
  };

  /** Calcula score ponderado en tiempo real según scores introducidos */
  const calculateB3Score = () => {
    if (!b3Criteria.length) return null;
    const totalWeight = b3Criteria.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);
    if (totalWeight === 0) return null;
    const weighted = b3Criteria.reduce((sum, c) => {
      const score = Number(b3CriteriaScores[c.name] || 0);
      return sum + (score * (Number(c.weight) || 0));
    }, 0);
    return Math.round((weighted / totalWeight) * 100) / 100;
  };

  /** Guardar revisión docente B3 */
  const saveB3Review = async () => {
    setIsSavingB3(true);
    try {
      const criteriaScores = b3Criteria.map(c => ({
        criterionId: c._id || c.name,
        name: c.name,
        description: c.description || '',
        weight: Number(c.weight) || 0,
        score: Number(b3CriteriaScores[c.name] || 0)
      }));
      const response = await apiClient.put(`/weekly-deliverables/${groupId}/${week}/b3-review`, {
        criteriaScores,
        instructorNotes: b3Notes,
        reviewedInClass: b3ReviewedInClass
      });
      if (response.data.success) {
        setB3SavedScore(response.data.overallScore);
        setSuccess('Revisión docente guardada exitosamente');
      }
    } catch (error) {
      setError('Error al guardar la revisión docente');
    } finally {
      setIsSavingB3(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── NIVEL 1: HERO – mismo patrón que Dashboard Docente ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">

            {/* Bloque izquierdo: breadcrumb + título */}
            <div>
              {/* Breadcrumb */}
              <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-1" aria-label="Breadcrumb">
                <button
                  onClick={() => navigate('/teaching/dashboard')}
                  className="hover:text-blue-600 transition-colors"
                >
                  Dashboard Docente
                </button>
                <span>/</span>
                <button
                  onClick={() => navigate(`/teaching/sections/${sectionId}/groups`)}
                  className="hover:text-blue-600 transition-colors"
                >
                  {section?.name || sectionId}
                </button>
                <span>/</span>
                <button
                  onClick={() => navigate('/teaching/dashboard')}
                  className="hover:text-blue-600 transition-colors"
                >
                  {group?.name || groupId}
                </button>
                <span>/</span>
                <span className="text-gray-600 font-medium">Semana {week}</span>
              </nav>

              {/* Título principal */}
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                {weekTitle || `Semana ${week}`}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {group?.name && <span className="font-medium text-gray-700">{group.name}</span>}
                {group?.name && section?.schedule && <span className="mx-2 text-gray-300">·</span>}
                {section?.schedule && <span>{section.schedule}</span>}
                {section?.semester && <span className="ml-2 text-gray-400">{section.semester}</span>}
              </p>
            </div>

            {/* Bloque derecho: estado + acciones */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {lastSaved && !hasUnsavedChanges && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  Guardado {new Date(lastSaved).toLocaleTimeString()}
                </span>
              )}
              {hasUnsavedChanges && (
                <span className="text-xs text-orange-500 font-medium">Sin guardar</span>
              )}
              {/* Botón Reabrir — visible solo para docente cuando la semana está Finalizada */}
              {isTeacher && (weeklyData?.status === 'Finalized' || weeklyData?.status === 'Completed') && (
                <button
                  onClick={handleReopenDeliverable}
                  disabled={isReopening}
                  className="flex items-center px-3 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Reabrir para que el grupo pueda editar de nuevo"
                >
                  <Unlock className="w-4 h-4 mr-1.5" />
                  {isReopening ? 'Reabriendo...' : 'Reabrir entregable'}
                </button>
              )}
              {weeklyData?.status === 'completed' && (
                <button
                  onClick={() => navigate(`/teaching/sections/${sectionId}/groups/${groupId}/cross-feedback/${week}`)}
                  className="flex items-center px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  title="Evaluar otros grupos"
                >
                  <Users className="w-4 h-4 mr-1.5" />
                  Feedback Cruzado
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex">
              <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex">
              <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
              <span className="text-green-700">{success}</span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

          {/* ── Columna principal (B1 → B2 → Coach → B5) ── */}
          <div className="lg:col-span-2 space-y-5">

          {/* B2: Trabajo Grupal */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Edit3 className="w-5 h-5 mr-2 text-blue-600" />
                    Trabajo Grupal
                  </h3>
                  <div className="flex items-center space-x-2">
                    {isTeacher ? (
                      // Docente: solo visualiza, no puede tomar el lock
                      isEditorLocked && lockOwner ? (
                        <span className="text-sm bg-blue-50 text-blue-600 px-3 py-1 rounded-full flex items-center">
                          <Edit3 className="w-4 h-4 mr-1" />
                          {lockOwnerName || 'Estudiante'} editando
                        </span>
                      ) : (
                        <span className="text-sm bg-gray-100 text-gray-500 px-3 py-1 rounded-full flex items-center">
                          <BookOpen className="w-4 h-4 mr-1" />
                          Vista docente
                        </span>
                      )
                    ) : (
                      // Estudiante: controles de lock
                      isEditorLocked ? (
                        lockOwner === user.id ? (
                          <button
                            onClick={releaseLock}
                            className="text-sm bg-red-100 text-red-700 px-3 py-1 rounded-full flex items-center"
                          >
                            <Unlock className="w-4 h-4 mr-1" />
                            Liberar
                          </button>
                        ) : (
                          <span className="text-sm bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full flex items-center">
                            <Lock className="w-4 h-4 mr-1" />
                            Editando: {lockOwnerName || 'un compañero'}
                          </span>
                        )
                      ) : (
                        <button
                          onClick={requestLock}
                          className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full flex items-center hover:bg-green-200"
                        >
                          <Edit3 className="w-4 h-4 mr-1" />
                          Editar
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4">
                {/* Instrucciones B2 */}
                {b2Instructions && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-900 whitespace-pre-line">{b2Instructions}</p>
                  </div>
                )}

                {/* Formulario dinámico B2 — solo lectura */}
                {b2FormFields.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {b2FormFields.map((field, idx) => {
                      const val = b2FormData[field.fieldId];
                      const isEmpty = val === undefined || val === null || String(val).trim() === '';
                      return (
                        <div key={field.fieldId || idx} className="py-3 first:pt-0 last:pb-0">
                          <p className="text-xs font-medium text-gray-500 mb-1">
                            {field.label}
                            {field.required && <span className="text-red-400 ml-1">*</span>}
                          </p>
                          {isEmpty ? (
                            <p className="text-sm text-gray-400 italic">Sin respuesta</p>
                          ) : (
                            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                              {String(val)}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  editorContent.trim() ? (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{editorContent}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Sin contenido registrado.</p>
                  )
                )}
              </div>
            </div>

            {/* B4: Coach IA Panel - AI-Powered Feedback */}
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Brain className="w-5 h-5 mr-2 text-purple-600" />
                    Coach IA
                  </h3>
                  {coachFeedback && (
                    <button
                      onClick={() => setShowCoachPanel(!showCoachPanel)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {showCoachPanel ? 
                        <ChevronUp className="w-4 h-4" /> : 
                        <ChevronDown className="w-4 h-4" />
                      }
                    </button>
                  )}
                </div>
              </div>
              <div className="p-4">
                <button
                  onClick={requestCoachHelp}
                  disabled={requestingCoachHelp || !editorContent.trim()}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                >
                  {requestingCoachHelp ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Analizando con IA...
                    </>
                  ) : (
                    <>
                      <Lightbulb className="w-4 h-4 mr-2" />
                      Solicitar Análisis IA
                    </>
                  )}
                </button>

                {!editorContent.trim() && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Escribe contenido para obtener feedback personalizado
                  </p>
                )}

                {coachFeedback && showCoachPanel && (
                  <div className="mt-4 space-y-3">
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <Award className="w-5 h-5 text-purple-600 mr-2" />
                        <h4 className="text-sm font-medium text-purple-900">Análisis IA Detallado</h4>
                      </div>
                      <div className="text-sm text-purple-800 whitespace-pre-line leading-relaxed">
                        {coachFeedback}
                      </div>
                      <div className="mt-3 pt-3 border-t border-purple-200">
                        <div className="flex items-center justify-between text-xs text-purple-600">
                          <span className="flex items-center">
                            <Zap className="w-3 h-3 mr-1" />
                            Powered by OpenAI
                          </span>
                          <button
                            onClick={() => setCoachFeedback(null)}
                            className="hover:text-purple-800"
                          >
                            Limpiar análisis
                          </button>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={requestCoachHelp}
                      disabled={requestingCoachHelp}
                      className="w-full text-purple-600 border border-purple-300 py-1 px-3 rounded text-sm hover:bg-purple-50 disabled:opacity-50"
                    >
                      Actualizar análisis
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* B5: Declaración Final */}
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <div 
                className="px-4 py-3 border-b border-gray-200 cursor-pointer"
                onClick={() => setShowFinalDeclaration(!showFinalDeclaration)}
              >
                <h3 className="text-lg font-semibold text-gray-900 flex items-center justify-between">
                  <span className="flex items-center">
                    <Target className="w-5 h-5 mr-2 text-green-600" />
                    Declaración Final Grupal
                  </span>
                  {showFinalDeclaration ? 
                    <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  }
                </h3>
              </div>

              {showFinalDeclaration && (
                <div className="p-4">
                  {/* Renderizar según formato del template */}
                  {b5Template.format === 'structured' && b5Template.sections && b5Template.sections.length > 0 ? (
                    // FORMATO ESTRUCTURADO: Secciones dinámicas
                    <div className="space-y-6">
                      {b5Template.sections.map((section) => (
                        <div key={section.id} className="border-l-4 border-green-500 pl-4">
                          <h4 className="font-semibold text-gray-900 mb-2">
                            {section.title}
                            {section.required && <span className="text-red-500 ml-1">*</span>}
                          </h4>
                          {section.description && (
                            <p className="text-sm text-gray-600 mb-3">{section.description}</p>
                          )}
                          
                          {/* Campos de la sección */}
                          <div className="space-y-3">
                            {(section.fields || []).map((field) => (
                              <div key={field.id}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {field.label}
                                  {field.required && <span className="text-red-500 ml-1">*</span>}
                                </label>
                                
                                {field.type === 'textarea' || field.type === 'markdown' ? (
                                  <textarea
                                    value={b5FormData[field.id] || ''}
                                    onChange={(e) => {
                                      setB5FormData({ ...b5FormData, [field.id]: e.target.value });
                                      setHasUnsavedChanges(true);
                                    }}
                                    placeholder={field.placeholder || ''}
                                    rows={4}
                                    disabled={isEditorLocked && lockOwner !== user.id}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                  />
                                ) : field.type === 'select' ? (
                                  <select
                                    value={b5FormData[field.id] || ''}
                                    onChange={(e) => {
                                      setB5FormData({ ...b5FormData, [field.id]: e.target.value });
                                      setHasUnsavedChanges(true);
                                    }}
                                    disabled={isEditorLocked && lockOwner !== user.id}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                  >
                                    <option value="">{field.placeholder || 'Selecciona una opción'}</option>
                                    {(field.options || []).map((opt, idx) => (
                                      <option key={idx} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                ) : field.type === 'number' ? (
                                  <input
                                    type="number"
                                    value={b5FormData[field.id] || ''}
                                    onChange={(e) => {
                                      setB5FormData({ ...b5FormData, [field.id]: e.target.value });
                                      setHasUnsavedChanges(true);
                                    }}
                                    placeholder={field.placeholder || ''}
                                    disabled={isEditorLocked && lockOwner !== user.id}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={b5FormData[field.id] || ''}
                                    onChange={(e) => {
                                      setB5FormData({ ...b5FormData, [field.id]: e.target.value });
                                      setHasUnsavedChanges(true);
                                    }}
                                    placeholder={field.placeholder || ''}
                                    disabled={isEditorLocked && lockOwner !== user.id}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // FORMATO LEGACY: Textarea genérico
                    <textarea
                      value={finalDeclaration}
                      onChange={(e) => {
                        setFinalDeclaration(e.target.value);
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Escriban aquí la declaración final del grupo sobre los aprendizajes de esta semana..."
                      className="w-full h-32 border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                      disabled={isEditorLocked && lockOwner !== user.id}
                    />
                  )}

                  {/* Resumen grupal B5 — solo lectura para docente */}
                  {(finalDeclaration || b5DraftSummary) && (
                    <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">
                        {finalDeclaration ? 'Resumen declarado (final):' : `Borrador del grupo${b5DraftEditedByName ? ` — editado por ${b5DraftEditedByName}` : ''}:`}
                      </p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">
                        {finalDeclaration || b5DraftSummary}
                      </p>
                    </div>
                  )}

                  {/* Confirmaciones del grupo — lectura */}
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Confirmaciones ({Object.keys(memberApprovals).length}/{group?.members?.length || 0})
                    </h4>
                    <div className="space-y-2">
                      {group?.members?.map(member => (
                        <div key={member.userId} className="flex items-center justify-between">
                          <span className="text-sm text-gray-700 flex items-center">
                            <User className="w-4 h-4 mr-2" />
                            {member.userName}
                          </span>
                          {memberApprovals[member.userId] ? (
                            <span className="flex items-center gap-1.5 text-green-600 text-sm">
                              {(() => {
                                const ts = memberApprovals[member.userId].timestamp;
                                if (ts) {
                                  const d = new Date(ts);
                                  const fecha = d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                                  const hora  = d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
                                  return <span className="text-xs text-gray-400">{fecha} {hora}</span>;
                                }
                                return null;
                              })()}
                              <CheckCircle className="w-4 h-4" /> Confirmado
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-gray-400 text-sm">
                              <Clock className="w-4 h-4" /> Pendiente
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Log de resets de confirmaciones */}
                  {b5ResetLog.length > 0 && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <p className="text-xs font-medium text-gray-400 mb-1">Historial de reinicios:</p>
                      <div className="space-y-1">
                        {[...b5ResetLog].reverse().map((entry, idx) => {
                          const d = new Date(entry.resetAt);
                          const fecha = d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: '2-digit' });
                          const hora  = d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
                          return (
                            <div key={idx} className="pl-1 border-l-2 border-amber-300">
                              <p className="text-xs text-amber-600 flex items-center gap-1">
                                <span>⚠</span>
                                <span>Confirmaciones reiniciadas por <strong>{entry.resetByName}</strong> al editar el borrador — {fecha} {hora}</span>
                              </p>
                              {entry.previousDraft && (
                                <p className="mt-1 ml-4 text-xs text-gray-400 italic whitespace-pre-wrap">
                                  Borrador anterior: &ldquo;{entry.previousDraft}&rdquo;
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Banner semana finalizada */}
                  {(weeklyData?.status === 'Finalized' || weeklyData?.status === 'Completed') && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Semana declarada completada por consenso
                      </p>
                      {finalDeclaration && (
                        <div className="mt-2 bg-white rounded p-3 border border-green-100">
                          <p className="text-xs text-gray-500 mb-1">Resumen declarado:</p>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{finalDeclaration}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* B3: Panel Revisión Docente — solo visible para teacher/admin */}
          {isTeacher && (
            <div className="bg-white rounded-lg shadow-sm border border-indigo-200">
              <div
                className="px-4 py-3 border-b border-indigo-200 cursor-pointer bg-indigo-50 rounded-t-lg"
                onClick={() => setShowB3Panel(!showB3Panel)}
              >
                <h3 className="text-lg font-semibold text-indigo-900 flex items-center justify-between">
                  <span className="flex items-center">
                    <ClipboardList className="w-5 h-5 mr-2 text-indigo-600" />
                    Revisión Docente (B3)
                  </span>
                  <div className="flex items-center space-x-2">
                    {b3SavedScore !== null && (
                      <span className="text-sm font-bold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
                        {b3SavedScore}/10
                      </span>
                    )}
                    {showB3Panel ? <ChevronUp className="w-5 h-5 text-indigo-400" /> : <ChevronDown className="w-5 h-5 text-indigo-400" />}
                  </div>
                </h3>
              </div>

              {showB3Panel && (
                <div className="p-4 space-y-4">
                  {b3Criteria.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-3">
                      No hay criterios B3 configurados para esta semana.
                      Confíguralos en el GroupCourse Builder.
                    </p>
                  ) : (
                    <>
                      {/* Criterios con inputs de score — grid 2 cols para aprovechar ancho */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {b3Criteria.map((criterion, idx) => (
                          <div key={idx} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1 pr-2">
                                <p className="text-sm font-medium text-gray-800">{criterion.name}</p>
                                {criterion.description && (
                                  <p className="text-xs text-gray-500 mt-0.5">{criterion.description}</p>
                                )}
                                <span className="text-xs text-indigo-600 font-medium">Peso: {criterion.weight}%</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Star className="w-4 h-4 text-yellow-400" />
                                <input
                                  type="number"
                                  min="0"
                                  max="10"
                                  step="0.5"
                                  value={b3CriteriaScores[criterion.name] ?? ''}
                                  onChange={e => setB3CriteriaScores(prev => ({
                                    ...prev,
                                    [criterion.name]: e.target.value
                                  }))}
                                  placeholder="0-10"
                                  className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Score ponderado en tiempo real */}
                      {calculateB3Score() !== null && (
                        <div className="flex items-center justify-between bg-indigo-50 rounded-lg p-3">
                          <span className="text-sm font-medium text-indigo-800 flex items-center">
                            <Award className="w-4 h-4 mr-1 text-indigo-600" />
                            Calificación ponderada
                          </span>
                          <span className="text-xl font-bold text-indigo-700">
                            {calculateB3Score()}/10
                          </span>
                        </div>
                      )}

                      {/* Notas del docente */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notas del docente
                        </label>
                        <textarea
                          value={b3Notes}
                          onChange={e => setB3Notes(e.target.value)}
                          placeholder="Observaciones, sugerencias, retroalimentación para el grupo..."
                          rows={4}
                          className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none"
                        />
                      </div>

                      {/* Pie: revisado en clase + botón guardar en línea */}
                      <div className="flex items-center justify-between pt-1">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={b3ReviewedInClass}
                            onChange={e => setB3ReviewedInClass(e.target.checked)}
                            className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">Revisado en clase</span>
                        </label>
                        <button
                          onClick={saveB3Review}
                          disabled={isSavingB3}
                          className="bg-indigo-600 text-white py-2 px-5 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors text-sm"
                        >
                          {isSavingB3 ? (
                            <><Loader2 className="w-4 h-4 animate-spin mr-2" />Guardando...</>
                          ) : (
                            <><CheckCircle className="w-4 h-4 mr-2" />Guardar revisión B3</>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          </div>{/* /columna-principal */}

          {/* ── Sidebar: Miembros · Comentarios · Recursos ── */}
          <div className="lg:col-span-1 space-y-5 lg:sticky lg:top-4">

          {/* Miembros del grupo */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-gray-600" />
                  Miembros del Grupo
                </h3>
              </div>
              <div className="p-4">
                <div className="space-y-2">
                  {group?.members?.map(member => (
                    <div key={member.userId} className="flex items-center space-x-2 text-sm">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className={member.userId === user.id ? 'font-medium text-blue-600' : 'text-gray-700'}>
                        {member.userName}
                        {member.userId === user.id && ' (tú)'}
                      </span>
                      {lockOwner === member.userId && (
                        <Lock className="w-4 h-4 text-yellow-500" title="Editando" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Comentarios individuales */}
            <div className="mt-5 bg-white rounded-lg shadow-sm border border-gray-200">
              <div 
                className="px-4 py-3 border-b border-gray-200 cursor-pointer"
                onClick={() => setShowComments(!showComments)}
              >
                <h3 className="text-base font-semibold text-gray-900 flex items-center justify-between">
                  <span className="flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2 text-blue-600" />
                    Comentarios ({comments.length})
                  </span>
                  {showComments ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </h3>
              </div>
              {showComments && (
                <div className="p-3">
                  <div className="space-y-3 mb-3 max-h-56 overflow-y-auto">
                    {comments.map(comment => (
                      <div key={comment.id} className="border-l-2 border-blue-200 pl-3">
                        <div className="flex items-center text-xs text-gray-500 mb-0.5">
                          <User className="w-3 h-3 mr-1" />
                          {comment.userName || 'Miembro'}
                          <span className="mx-1">•</span>
                          {comment.timestamp || comment.createdAt ? new Date(comment.timestamp || comment.createdAt).toLocaleString() : '—'}
                        </div>
                        <p className="text-sm text-gray-700">{comment.content || comment.comment}</p>
                      </div>
                    ))}
                    {comments.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-3">Sin comentarios aún</p>
                    )}
                  </div>
                  {!isTeacher && (
                    <div className="border-t border-gray-100 pt-3">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Añadir comentario..."
                        className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                        rows={2}
                      />
                      <button onClick={addComment} disabled={!newComment.trim()}
                        className="mt-1.5 w-full bg-blue-600 text-white py-1.5 px-3 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center">
                        <MessageSquare className="w-3.5 h-3.5 mr-1" />Comentar
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Recursos y Pautas */}
            <div className="mt-5 bg-white rounded-lg shadow-sm border border-gray-200">
              <div
                className="px-4 py-3 border-b border-gray-200 cursor-pointer"
                onClick={() => setShowResources(!showResources)}
              >
                <h3 className="text-base font-semibold text-gray-900 flex items-center justify-between">
                  <span className="flex items-center">
                    <BookOpen className="w-4 h-4 mr-2 text-blue-600" />
                    Recursos y Pautas
                  </span>
                  {showResources ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </h3>
              </div>
              {showResources && (
                <div className="p-3 space-y-2">
                  {learningObjectives.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                      <h4 className="text-xs font-semibold text-blue-900 mb-1 flex items-center">
                        <Target className="w-3.5 h-3.5 mr-1" />Objetivos
                      </h4>
                      <ul className="space-y-0.5">
                        {learningObjectives.map((obj, idx) => (
                          <li key={idx} className="text-xs text-blue-800 flex items-start">
                            <span className="mr-1">•</span><span>{obj}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {weekResources.length > 0 ? weekResources.map((resource, index) => (
                    <div key={index} className="border border-gray-100 rounded-lg p-2.5 hover:bg-gray-50">
                      <div className="flex items-start">
                        {resource.type === 'video' && <Video className="w-4 h-4 text-red-500 mr-2 mt-0.5 shrink-0" />}
                        {resource.type === 'document' && <FileText className="w-4 h-4 text-blue-500 mr-2 mt-0.5 shrink-0" />}
                        {resource.type === 'link' && <ExternalLink className="w-4 h-4 text-green-500 mr-2 mt-0.5 shrink-0" />}
                        <div>
                          <p className="text-xs font-medium text-gray-900">{resource.title}</p>
                          <a href={resource.url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-700">Abrir →</a>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <p className="text-xs text-gray-500 text-center py-3">Sin recursos esta semana</p>
                  )}
                </div>
              )}
            </div>

            {/* B3 se muestra en columna principal — mejor espacio para edición */}
            {false && (
              <div className="mt-6 bg-white rounded-lg shadow-sm border border-indigo-200">
                <div
                  className="px-4 py-3 border-b border-indigo-200 cursor-pointer bg-indigo-50 rounded-t-lg"
                  onClick={() => setShowB3Panel(!showB3Panel)}
                >
                  <h3 className="text-lg font-semibold text-indigo-900 flex items-center justify-between">
                    <span className="flex items-center">
                      <ClipboardList className="w-5 h-5 mr-2 text-indigo-600" />
                      Revisión Docente (B3)
                    </span>
                    <div className="flex items-center space-x-2">
                      {b3SavedScore !== null && (
                        <span className="text-sm font-bold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded-full">
                          {b3SavedScore}/10
                        </span>
                      )}
                      {showB3Panel ? <ChevronUp className="w-5 h-5 text-indigo-400" /> : <ChevronDown className="w-5 h-5 text-indigo-400" />}
                    </div>
                  </h3>
                </div>

                {showB3Panel && (
                  <div className="p-4 space-y-4">
                    {b3Criteria.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-3">
                        No hay criterios B3 configurados para esta semana.
                        Confíguralos en el GroupCourse Builder.
                      </p>
                    ) : (
                      <>
                        {/* Criterios con inputs de score */}
                        <div className="space-y-3">
                          {b3Criteria.map((criterion, idx) => (
                            <div key={idx} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 pr-2">
                                  <p className="text-sm font-medium text-gray-800">{criterion.name}</p>
                                  {criterion.description && (
                                    <p className="text-xs text-gray-500 mt-0.5">{criterion.description}</p>
                                  )}
                                  <span className="text-xs text-indigo-600 font-medium">Peso: {criterion.weight}%</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Star className="w-4 h-4 text-yellow-400" />
                                  <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    step="0.5"
                                    value={b3CriteriaScores[criterion.name] ?? ''}
                                    onChange={e => setB3CriteriaScores(prev => ({
                                      ...prev,
                                      [criterion.name]: e.target.value
                                    }))}
                                    placeholder="0-10"
                                    className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Score ponderado en tiempo real */}
                        {calculateB3Score() !== null && (
                          <div className="flex items-center justify-between bg-indigo-50 rounded-lg p-3">
                            <span className="text-sm font-medium text-indigo-800 flex items-center">
                              <Award className="w-4 h-4 mr-1 text-indigo-600" />
                              Calificación ponderada
                            </span>
                            <span className="text-xl font-bold text-indigo-700">
                              {calculateB3Score()}/10
                            </span>
                          </div>
                        )}

                        {/* Notas del docente */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Notas del docente
                          </label>
                          <textarea
                            value={b3Notes}
                            onChange={e => setB3Notes(e.target.value)}
                            placeholder="Observaciones, sugerencias, retroalimentación para el grupo..."
                            rows={4}
                            className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none"
                          />
                        </div>

                        {/* Revisado en clase */}
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={b3ReviewedInClass}
                            onChange={e => setB3ReviewedInClass(e.target.checked)}
                            className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">Revisado en clase</span>
                        </label>

                        {/* Botón guardar */}
                        <button
                          onClick={saveB3Review}
                          disabled={isSavingB3}
                          className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                        >
                          {isSavingB3 ? (
                            <><Loader2 className="w-4 h-4 animate-spin mr-2" />Guardando...</>
                          ) : (
                            <><CheckCircle className="w-4 h-4 mr-2" />Guardar revisión B3</>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>{/* /sidebar-contextual */}

        </div>{/* /grid */}
      </div>{/* /container */}
    </div>
  );
};

export default WeeklyDeliverableEditor;